import React, { useState } from "react";
import { PageSection } from "@/components/AppLayout.jsx";
import { AdminGuard } from "@/components/admin/AdminGuard.jsx";
import { useI18n } from "@/coquille-bridge";
import { useDataStore } from "@/components/admin/useDataStore.js";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import {
  BeltsListPanel,
  ProgressOverviewPanel,
  ProgressDetailPanel,
  ExamsListPanel,
  StudentProgressView,
  ParentProgressView,
  useProgressScope,
} from "@/components/progress/KarateProgressPanels.jsx";

function useLocale() {
  const { lang } = useI18n();
  return lang === "en" ? "en" : "fr";
}

// ——— Admin ———

export function AdminBeltsPage() {
  const locale = useLocale();
  return (
    <AdminGuard>
      <PageSection
        title={locale === "en" ? "Belts / grades" : "Ceintures / grades"}
        subtitle={
          locale === "en"
            ? "Demo belt ladder — official requirements pending."
            : "Échelle démo — exigences officielles en attente."
        }
        testId="page-admin-ceintures"
      >
        <BeltsListPanel allowCrud />
      </PageSection>
    </AdminGuard>
  );
}

export function AdminProgressionPage() {
  const locale = useLocale();
  const { localizeDemo } = useKarateUi();
  const store = useDataStore();
  const [selectedStudent, setSelectedStudent] = useState(null);

  return (
    <AdminGuard>
      <PageSection
        title={locale === "en" ? "Student progress" : "Progression étudiants"}
        testId="page-admin-progression"
      >
        <ProgressOverviewPanel testId="admin-progress-overview" />
        <div className="progress-student-picker" style={{ marginTop: "1rem" }}>
          <label>
            {locale === "en" ? "View student detail" : "Voir détail étudiant"}
            <select
              value={selectedStudent || ""}
              onChange={(e) => setSelectedStudent(e.target.value || null)}
              data-testid="admin-progress-student-select"
            >
              <option value="">—</option>
              {store.progressions.map((p) => {
                const s = store.etudiants.find((e) => e.id === p.student_id);
                return (
                  <option key={p.student_id} value={p.student_id}>
                    {s ? localizeDemo(`${s.prenom} ${s.nom}`.trim()) : p.student_id}
                  </option>
                );
              })}
            </select>
          </label>
        </div>
        {selectedStudent ? (
          <ProgressDetailPanel studentId={selectedStudent} testId="admin-progress-detail" />
        ) : null}
      </PageSection>
    </AdminGuard>
  );
}

export function AdminExamensPage() {
  const locale = useLocale();
  return (
    <AdminGuard>
      <PageSection
        title={locale === "en" ? "Light exams" : "Examens légers"}
        subtitle={
          locale === "en"
            ? "Linked to events where applicable — no official certification."
            : "Liés aux événements si applicable — pas de certification officielle."
        }
        testId="page-admin-examens"
      >
        <ExamsListPanel allowCrud scope={{ mode: "admin" }} />
      </PageSection>
    </AdminGuard>
  );
}

// ——— Sensei ———

export function SenseiProgressionPage() {
  const locale = useLocale();
  const { localizeDemo } = useKarateUi();
  const scope = useProgressScope();
  const store = useDataStore();
  const [selected, setSelected] = useState(store.progressions.find((p) => {
    const s = store.etudiants.find((e) => e.id === p.student_id);
    return s?.dojo_id === scope.dojoId;
  })?.student_id || null);

  return (
    <PageSection
      title={locale === "en" ? "Student progress" : "Progression étudiants"}
      subtitle={locale === "en" ? "Track and update demo progression" : "Suivi et mise à jour démo"}
      testId="page-sensei-progression"
    >
      <ProgressOverviewPanel scope={scope} testId="sensei-progress-overview" />
      <label style={{ display: "block", marginTop: "1rem" }}>
        {locale === "en" ? "Student" : "Étudiant"}
        <select value={selected || ""} onChange={(e) => setSelected(e.target.value)} data-testid="sensei-progress-student-select">
          {store.progressions
            .filter((p) => {
              const s = store.etudiants.find((e) => e.id === p.student_id);
              return s?.dojo_id === scope.dojoId;
            })
            .map((p) => {
              const s = store.etudiants.find((e) => e.id === p.student_id);
              return (
                <option key={p.student_id} value={p.student_id}>
                  {localizeDemo(`${s?.prenom ?? ""} ${s?.nom ?? ""}`.trim()) || p.student_id}
                </option>
              );
            })}
        </select>
      </label>
      {selected ? (
        <ProgressDetailPanel studentId={selected} canEditSensei testId="sensei-progress-detail" />
      ) : null}
    </PageSection>
  );
}

export function SenseiExamensPage() {
  const locale = useLocale();
  const scope = useProgressScope();
  return (
    <PageSection title={locale === "en" ? "Exams" : "Examens"} testId="page-sensei-examens">
      <ExamsListPanel scope={scope} testId="sensei-exams-list" />
    </PageSection>
  );
}

export function SenseiBeltsPage() {
  const locale = useLocale();
  return (
    <PageSection title={locale === "en" ? "Belts reference" : "Référence ceintures"} testId="page-sensei-ceintures">
      <BeltsListPanel testId="sensei-belts-list" />
    </PageSection>
  );
}

// ——— Sempai ———

export function SempaiProgressionPage() {
  const locale = useLocale();
  const scope = useProgressScope();
  const store = useDataStore();
  const firstStudent = store.progressions.find((p) => {
    const s = store.etudiants.find((e) => e.id === p.student_id);
    return s?.dojo_id === scope.dojoId;
  })?.student_id;

  return (
    <PageSection
      title={locale === "en" ? "Progress (consult)" : "Progression (consultation)"}
      subtitle={
        locale === "en"
          ? "Assistant role — observe only, no grade validation."
          : "Rôle assistant — observation seulement, pas de validation de grade."
      }
      testId="page-sempai-progression"
    >
      <p className="muted" data-testid="sempai-progress-limits">
        {locale === "en"
          ? "You can add observations but cannot recommend exams or validate belts."
          : "Vous pouvez ajouter des observations mais pas recommander d'examen ni valider un grade."}
      </p>
      <ProgressOverviewPanel scope={scope} testId="sempai-progress-overview" />
      {firstStudent ? (
        <ProgressDetailPanel
          studentId={firstStudent}
          canEditSempaiNote
          testId="sempai-progress-detail"
        />
      ) : null}
    </PageSection>
  );
}

export function SempaiBeltsPage() {
  const locale = useLocale();
  return (
    <PageSection title={locale === "en" ? "Belts reference" : "Référence ceintures"} testId="page-sempai-ceintures">
      <BeltsListPanel testId="sempai-belts-list" />
    </PageSection>
  );
}

// ——— Étudiant ———

export function MaProgressionPage() {
  const locale = useLocale();
  const scope = useProgressScope();
  return (
    <PageSection
      title={locale === "en" ? "My progress" : "Ma progression"}
      subtitle={locale === "en" ? "Belt journey — Phase 1 demo" : "Parcours ceinture — démo Phase 1"}
      testId="page-ma-progression"
    >
      <StudentProgressView testId="student-progress-detail" />
      <ExamsListPanel scope={scope} testId="student-exams-list" />
    </PageSection>
  );
}

// ——— Parent ———

export function ParentProgressPage() {
  const locale = useLocale();
  const scope = useProgressScope();
  return (
    <PageSection
      title={locale === "en" ? "Child progress" : "Progression enfant"}
      testId="page-parent-progression"
    >
      <ParentProgressView testId="parent-progress-summary" />
      <ExamsListPanel scope={scope} testId="parent-exams-list" />
    </PageSection>
  );
}
